import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { authApi, type User, type LoginRequest } from '@/lib/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  ssoExchange: (token: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await authApi.me();
      setUser(response);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Capture sso_token presence synchronously during the render phase (before
  // any effects run). React fires child effects before parent effects, so
  // LoginPage's useEffect can clear the URL via replaceState before this
  // provider's useEffect reads it.  A useRef initializer runs during render,
  // guaranteeing we snapshot the URL before any effect in the tree executes.
  const ssoTokenPending = useRef(
    new URLSearchParams(globalThis.location.search).has('sso_token')
  );

  useEffect(() => {
    // Skip initial auth check if an SSO token is pending exchange.
    // The LoginPage will handle authentication via ssoExchange().
    // This prevents a race condition where checkAuth() resolves after
    // ssoExchange() and overwrites the authenticated user back to null.
    if (ssoTokenPending.current) {
      setIsLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Listen for 401 "session expired" events dispatched by the api wrapper.
  // Instead of a hard window.location.href redirect (which destroys React state),
  // we clear the user so ProtectedRoute's <Navigate to="/login" /> fires
  // as a soft SPA navigation.
  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
    };
    globalThis.addEventListener('auth:session-expired', handleSessionExpired);
    return () => {
      globalThis.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  const login = useCallback(async (credentials: LoginRequest) => {
    const response = await authApi.login(credentials);
    setUser(response.user);
  }, []);

  const ssoExchange = useCallback(async (token: string) => {
    const response = await authApi.exchangeSsoToken(token);
    setUser(response.user);
  }, []);

  const logout = useCallback(() => {
    authApi.logout().catch(() => {});
    setUser(null);
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      ssoExchange,
      logout,
    }),
    [user, isLoading, login, ssoExchange, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
